import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import {
  DEFAULT_PERIODS, averageOf, descriptorFor, fetchGradingPeriods, periodsForOffering, withLegacyPeriods,
} from "../../utils/gradingPeriods";
import s from "./Grades.module.css";
const DRAFT_KEY = (offId) => `gradebook.draft.${offId}`;
const DRAFT_SAVE_DEBOUNCE_MS = 800;
const DRAFT_SCHEMA_VERSION = 1;

/** Stable avatar color per name — same idiom used elsewhere. */
const AVATAR_COLORS = ["#5a4e3a", "#3a4e4a", "#4a3a5a", "#4e4a3a", "#3a4a5a", "#5a3a3a", "#3a5a3a"];
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(first, last) {
  const a = (first || "").trim()[0] || "";
  const b = (last  || "").trim()[0] || "";
  return (a + b).toUpperCase() || "?";
}

/** Tone class for the score input — green/amber/red bands matching the
 *  PH education convention (75 passing, 60–74 borderline, <60 failing).
 *  Empty / non-numeric values get the neutral default. */
function scoreToneClass(raw) {
  if (raw === "" || raw == null) return "";
  const n = Number(raw);
  if (Number.isNaN(n)) return "";
  if (n >= 75) return s.scoreHigh;
  if (n >= 60) return s.scoreMid;
  return s.scoreLow;
}

/** Format a clean average for the stats strip. Returns "—" on no data. */
function fmtAvg(values) {
  if (values.length === 0) return "—";
  const sum = values.reduce((a, b) => a + b, 0);
  return (sum / values.length).toFixed(1);
}

/** Sanitize one cell's pasted value — strip whitespace, drop currency
 *  glyphs / commas, clamp to [0, 100]. Returns "" for unparseable input
 *  so a stray header row in the paste lands as a blank cell rather than
 *  a NaN score row that the save endpoint would have to reject. */
function sanitizePastedScore(raw) {
  if (raw == null) return "";
  const cleaned = String(raw).trim().replace(/[,$₱]/g, "");
  if (cleaned === "") return "";
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return "";
  // Clamp; preserve up to two decimals to match the input's step="0.01".
  const clamped = Math.max(0, Math.min(100, n));
  return (Math.round(clamped * 100) / 100).toString();
}

export default function TeacherGradeEncodingPage() {
  const { id } = useParams();
  const classOfferingId = Number(id);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [busy, setBusy]       = useState(false);
  // toast: { kind: "success" | "error", text } — auto-dismisses via animation.
  const [toast, setToast]     = useState(null);
  const toastTimer = useRef(null);

  const [offering, setOffering]   = useState(null);
  const [subject, setSubject]     = useState(null);
  const [section, setSection]     = useState(null);
  const [semester, setSemester]   = useState(null);
  const [students, setStudents]   = useState([]);
  // Grading periods for this class (1st/2nd Quarter, Midterm/Final…) —
  // from Settings → Grading Periods for the section's Level + semester.
  const [periods, setPeriods] = useState(DEFAULT_PERIODS);
  const PERIODS = useMemo(() => periods.map(x => x.code), [periods]);
  const labelOf = (code) => periods.find(x => x.code === code)?.label || code;
  // Which tab is open: one period code (encode that quarter) or "summary".
  const [tab, setTab] = useState(null);
  // Student opened from the Summary tab → full-record popup.
  const [detailStudent, setDetailStudent] = useState(null);
  // grades[studentId] = { [periodCode]: { score, remarks } }
  const [grades, setGrades]             = useState({});
  // Snapshot at load (or after save) — so we can derive dirty rows live.
  const [originalGrades, setOriginal]   = useState({});

  // ── Class switcher state ──
  // Teacher's full list of class offerings — populated after the main load
  // so the switcher dropdown can offer one-click jumps without bouncing
  // back to the dashboard.
  const [siblingOfferings, setSiblingOfferings] = useState([]);
  const [siblingLookups,   setSiblingLookups]   = useState({ subjects: {}, sections: {}, semesters: {} });
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef(null);

  // ── Draft restore state ──
  // When the page mounts, we look for a stored draft. If present AND it
  // differs from the just-loaded baseline, we surface a "Restore unsaved
  // changes?" banner instead of silently overwriting either side. The
  // banner shows once per mount; declining clears the draft so it doesn't
  // re-prompt forever.
  const [pendingDraft, setPendingDraft] = useState(null); // null | { grades, savedAt, dirtyCount }
  const draftPromptedRef = useRef(false);
  const draftTimerRef    = useRef(null);

  // ── Keyboard navigation ──
  // Refs to every score input so Enter/ArrowDown/ArrowUp can move the
  // focus along the column without React re-renders.
  const scoreInputRefs = useRef(new Map());
  const setScoreRef = useCallback((studentId, period, el) => {
    const key = `${studentId}-${period}`;
    if (el) scoreInputRefs.current.set(key, el);
    else    scoreInputRefs.current.delete(key);
  }, []);

  /* ── Data load ──────────────────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        // The class-offerings list is small and the existing endpoint has
        // no /{id} GET, so we still pull all and pluck ours. Same as the
        // legacy implementation — keeps the load contract identical.
        const off = await fetch(`${API_BASE}/api/class-offerings`).then(r => r.json());
        const me = off.find(c => c.id === classOfferingId);
        if (!me) throw new Error("Class offering not found");

        const [subjects, sections, semesters, allStudents, existing, gradeLevels, periodRows] = await Promise.all([
          fetch(`${API_BASE}/api/subjects`).then(r => r.json()),
          fetch(`${API_BASE}/api/sections`).then(r => r.json()),
          fetch(`${API_BASE}/api/semesters`).then(r => r.json()),
          fetch(`${API_BASE}/api/students?sectionId=${me.sectionId}`).then(r => r.json()),
          fetch(`${API_BASE}/api/grades?classOfferingId=${classOfferingId}`).then(r => r.json()),
          fetch(`${API_BASE}/api/grade-levels`).then(r => (r.ok ? r.json() : [])).catch(() => []),
          fetchGradingPeriods(),
        ]);
        if (cancelled) return;

        // Columns = this Level's periods for this semester, plus any codes
        // already holding grades (so older Midterm/Final scores stay visible).
        const classPeriods = withLegacyPeriods(
          periodsForOffering({ offering: me, sections, gradeLevels, semesters, rows: periodRows }),
          existing
        );
        setPeriods(classPeriods);
        const classCodes = classPeriods.map(x => x.code);
        // Open on the first period that still has blanks; all done → Summary.
        const firstOpen = classPeriods.find(per => !per.legacy && allStudents.some(st => {
          const sc = existing.find(r => r.studentId === st.id && r.period === per.code)?.score;
          return sc == null;
        }));
        setTab(firstOpen ? firstOpen.code : "summary");

        setOffering(me);
        setSubject(subjects.find(x => x.id === me.subjectId) || null);
        setSection(sections.find(x => x.id === me.sectionId) || null);
        setSemester(semesters.find(x => x.id === me.semesterId) || null);
        setStudents(allStudents);

        const g = {};
        existing.forEach(row => {
          if (!g[row.studentId]) g[row.studentId] = {};
          g[row.studentId][row.period] = {
            score: row.score == null ? "" : String(row.score),
            remarks: row.remarks || "",
          };
        });
        setGrades(g);
        // Deep clone — without this, original would mutate alongside grades
        // and the dirty-row highlight would never fire.
        setOriginal(JSON.parse(JSON.stringify(g)));

        // Populate the class switcher: every offering this teacher owns,
        // minus the current one. Pulled from the already-fetched `off`
        // list so we don't fire an extra request. Lookups are reused for
        // pretty labels in the dropdown.
        if (me.teacherStaffId != null) {
          const mine = off.filter(c => c.teacherStaffId === me.teacherStaffId);
          setSiblingOfferings(mine);
          setSiblingLookups({
            subjects:  Object.fromEntries(subjects.map(r => [r.id, r])),
            sections:  Object.fromEntries(sections.map(r => [r.id, r])),
            semesters: Object.fromEntries(semesters.map(r => [r.id, r])),
          });
        } else {
          setSiblingOfferings([]);
        }

        // Probe for a stashed draft from a previous session for this
        // exact offering. We compare against the just-loaded baseline so
        // we don't prompt when the draft is identical (e.g. a no-op
        // refresh after a successful save).
        try {
          const raw = window.localStorage.getItem(DRAFT_KEY(classOfferingId));
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && parsed.schema === DRAFT_SCHEMA_VERSION && parsed.grades) {
              const sameAsServer =
                JSON.stringify(parsed.grades) === JSON.stringify(g);
              if (!sameAsServer && !draftPromptedRef.current) {
                draftPromptedRef.current = true;
                // Count cell-level differences so the banner can be specific.
                const diff = countCellDiff(parsed.grades, g, classCodes);
                setPendingDraft({
                  grades: parsed.grades,
                  savedAt: parsed.savedAt || null,
                  dirtyCount: diff,
                });
              } else if (sameAsServer) {
                // Stale draft equal to server — clean up so we don't probe
                // again next time.
                window.localStorage.removeItem(DRAFT_KEY(classOfferingId));
              }
            }
          }
        } catch { /* localStorage unavailable / quota — non-fatal */ }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [classOfferingId]);

  /* ── Toast helper (auto-dismiss after CSS exit) ─────────────────── */
  function showToast(kind, text) {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ kind, text });
    // CSS keeps the toast visible 2.5s then fades 280ms — we drop it from
    // state shortly after so a second save can re-trigger the animation.
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  function setCell(studentId, period, field, value) {
    setGrades(prev => {
      const row = { ...(prev[studentId] || {}) };
      row[period] = { ...(row[period] || { score: "", remarks: "" }), [field]: value };
      return { ...prev, [studentId]: row };
    });
  }

  /* ── Dirty tracking — drives the sticky save bar + row highlight ── */
  function isCellDirty(studentId, period) {
    const cur  = grades[studentId]?.[period]   || { score: "", remarks: "" };
    const orig = originalGrades[studentId]?.[period] || { score: "", remarks: "" };
    return cur.score !== orig.score || cur.remarks !== orig.remarks;
  }
  function isRowDirty(studentId) {
    return PERIODS.some(p => isCellDirty(studentId, p));
  }
  const dirtyCount = useMemo(() => {
    let n = 0;
    const ids = new Set([...Object.keys(grades), ...Object.keys(originalGrades)]);
    for (const id of ids) {
      for (const p of PERIODS) {
        if (isCellDirty(Number(id), p)) n++;
      }
    }
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grades, originalGrades, PERIODS]);

  /* ── Draft auto-save ─────────────────────────────────────────────
   * Debounced write to localStorage whenever `grades` changes AND we're
   * past the initial load. When the form goes clean (dirtyCount === 0)
   * we remove the draft so a stale entry can't trigger a future false
   * restore prompt. The draft never includes anything other than the
   * editable state — no auth, no PII beyond the score/remarks pair the
   * teacher is already typing.
   * ─────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (loading) return undefined;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);

    if (dirtyCount === 0) {
      // Clean form — clear any prior draft so next mount doesn't prompt.
      try { window.localStorage.removeItem(DRAFT_KEY(classOfferingId)); }
      catch { /* non-fatal */ }
      return undefined;
    }

    draftTimerRef.current = setTimeout(() => {
      try {
        const payload = JSON.stringify({
          schema: DRAFT_SCHEMA_VERSION,
          savedAt: new Date().toISOString(),
          grades,
        });
        window.localStorage.setItem(DRAFT_KEY(classOfferingId), payload);
      } catch { /* quota exceeded / disabled — non-fatal */ }
    }, DRAFT_SAVE_DEBOUNCE_MS);

    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [grades, dirtyCount, classOfferingId, loading]);

  /* ── Live stats ─────────────────────────────────────────────────── */
  const stats = useMemo(() => {
    const perPeriod = periods.map(per => {
      const vals = [];
      for (const st of students) {
        const c = grades[st.id]?.[per.code];
        if (c?.score === "" || c?.score == null) continue;
        const n = Number(c.score);
        if (!Number.isNaN(n)) vals.push(n);
      }
      return { code: per.code, label: per.label, encoded: vals.length, avg: fmtAvg(vals) };
    });
    return { total: students.length, perPeriod };
  }, [students, grades, periods]);

  /** Period column(s) shown in the encode table — just the open tab. */
  const tabCodes = useMemo(
    () => (tab && tab !== "summary" ? [tab] : []),
    [tab]
  );

  /* ── Save ────────────────────────────────────────────────────────── */
  async function saveAll() {
    setBusy(true); setError("");
    try {
      const entries = [];
      students.forEach(st => {
        PERIODS.forEach(p => {
          const cell = grades[st.id]?.[p];
          if (cell && (cell.score !== "" || (cell.remarks && cell.remarks.trim() !== ""))) {
            entries.push({
              studentId: st.id,
              period: p,
              score: cell.score === "" ? null : cell.score,
              remarks: cell.remarks || null,
            });
          } else if (cell) {
            // Both blank → tell server to delete any prior row.
            entries.push({ studentId: st.id, period: p, score: null, remarks: null });
          }
        });
      });
      const res = await fetch(`${API_BASE}/api/grades/bulk`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ classOfferingId, entries }),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      }
      const result = await res.json();
      // Refresh "original" snapshot so the sticky bar collapses and dirty
      // highlights clear without a re-fetch.
      setOriginal(JSON.parse(JSON.stringify(grades)));
      // Clear the draft now that the server has the source of truth.
      try { window.localStorage.removeItem(DRAFT_KEY(classOfferingId)); }
      catch { /* non-fatal */ }
      showToast(
        "success",
        `Saved ${result.saved} grade${result.saved === 1 ? "" : "s"}` +
        (result.deleted ? ` · removed ${result.deleted}` : "")
      );
    } catch (e) {
      showToast("error", e.message || "Save failed");
    }
    finally { setBusy(false); }
  }

  /* ── Draft restore handlers ─────────────────────────────────────── */
  function applyDraft() {
    if (!pendingDraft) return;
    setGrades(pendingDraft.grades);
    setPendingDraft(null);
    showToast("success", `Restored ${pendingDraft.dirtyCount} unsaved change${pendingDraft.dirtyCount === 1 ? "" : "s"}.`);
  }
  function discardDraft() {
    setPendingDraft(null);
    try { window.localStorage.removeItem(DRAFT_KEY(classOfferingId)); }
    catch { /* non-fatal */ }
  }

  /* ── Class-switcher outside-click + Escape ───────────────────────── */
  useEffect(() => {
    if (!switcherOpen) return undefined;
    function onDown(e) {
      if (!switcherRef.current) return;
      if (!switcherRef.current.contains(e.target)) setSwitcherOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setSwitcherOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [switcherOpen]);

  /* ── Keyboard navigation between score cells ─────────────────────── */
  function focusScore(rowIndex, period) {
    const target = students[rowIndex];
    if (!target) return false;
    const el = scoreInputRefs.current.get(`${target.id}-${period}`);
    if (!el) return false;
    el.focus();
    // Move cursor to end so a follow-up keystroke types after the value
    // rather than replacing it.
    try {
      const v = el.value || "";
      el.setSelectionRange(v.length, v.length);
    } catch { /* some browsers reject setSelectionRange on number inputs */ }
    return true;
  }

  function onScoreKeyDown(e, rowIndex, period) {
    // Enter / Shift+Enter → move down / up within the same column.
    // ArrowDown / ArrowUp do the same so a teacher using arrows out of
    // habit gets the same behavior. We only intercept when the input has
    // focus and the cursor isn't being used to navigate inside a value.
    if (e.key === "Enter" || (e.key === "ArrowDown" && !e.shiftKey)) {
      e.preventDefault();
      const dir = e.shiftKey && e.key === "Enter" ? -1 : 1;
      const next = rowIndex + dir;
      if (next >= 0 && next < students.length) focusScore(next, period);
      return;
    }
    if (e.key === "ArrowUp" && !e.shiftKey) {
      e.preventDefault();
      if (rowIndex > 0) focusScore(rowIndex - 1, period);
      return;
    }
  }

  /* ── Paste-from-spreadsheet ──────────────────────────────────────────
   * If the clipboard payload contains newlines, treat it as a column
   * paste: split by newline, take the first whitespace/tab-separated
   * column of each row (so a copy of a [Score, Remarks] pair still
   * lands the scores correctly), and fill from the current row down.
   * Single-value pastes fall through to the input's default behavior
   * — no surprises for ordinary copy/paste of one cell.
   * ──────────────────────────────────────────────────────────────── */
  function onScorePaste(e, rowIndex, period) {
    const text = e.clipboardData?.getData("text") ?? "";
    if (!text.includes("\n") && !text.includes("\r")) return; // single-cell paste → default
    e.preventDefault();
    const lines = text.split(/\r\n|\r|\n/).filter(l => l !== "" || true); // keep blanks
    let applied = 0;
    setGrades(prev => {
      const next = { ...prev };
      for (let i = 0; i < lines.length; i++) {
        const target = students[rowIndex + i];
        if (!target) break;
        const cellRaw = lines[i].split(/\t|  +/)[0]; // first column only
        const sanitized = sanitizePastedScore(cellRaw);
        const row = { ...(next[target.id] || {}) };
        row[period] = {
          ...(row[period] || { score: "", remarks: "" }),
          score: sanitized,
        };
        next[target.id] = row;
        applied++;
      }
      return next;
    });
    if (applied > 0) showToast("success", `Pasted ${applied} score${applied === 1 ? "" : "s"}.`);
  }

  /* ── Header text ─────────────────────────────────────────────────── */
  const headingTitle = subject?.title || (offering ? `Subject #${offering.subjectId}` : "Class");

  // Compute switcher items — everyone except the current offering, sorted
  // by semester then subject so they read predictably. Each carries the
  // resolved labels so the dropdown doesn't have to look anything up.
  const switcherItems = useMemo(() => {
    if (siblingOfferings.length === 0) return [];
    return siblingOfferings
      .map(c => {
        const subj = siblingLookups.subjects[c.subjectId] || null;
        const sec  = siblingLookups.sections[c.sectionId] || null;
        const sem  = siblingLookups.semesters[c.semesterId] || null;
        return {
          id: c.id,
          isCurrent: c.id === classOfferingId,
          title: subj?.title || `Subject #${c.subjectId}`,
          code:  subj?.code  || "",
          sectionName: sec?.name || "",
          semesterLabel: sem?.label || "",
        };
      })
      .sort((a, b) => {
        if (a.isCurrent && !b.isCurrent) return -1;
        if (!a.isCurrent && b.isCurrent) return 1;
        return (a.semesterLabel + a.title).localeCompare(b.semesterLabel + b.title);
      });
  }, [siblingOfferings, siblingLookups, classOfferingId]);

  return (
    <div className={s.page}>
      {/* Premium header with breadcrumb + meta pills */}
      <div className={s.headerUpgraded}>
        <button className={s.backBtnPremium} onClick={() => navigate("/dashboard")}>
          ← Back
        </button>
        <div>
          <div className={s.eyebrow}>Teacher · Grade encoding</div>
          <h1 className={s.title} style={{ marginBottom: 2 }}>
            {loading ? "Loading…" : headingTitle}
          </h1>
          {!loading && (subject?.code || section?.name || semester?.label) && (
            <div className={s.headerMetaRow}>
              {subject?.code && (
                <span className={`${s.headerPill} ${s.headerPillCode}`}>{subject.code}</span>
              )}
              {section?.name  && <span className={s.headerPill}>Section {section.name}</span>}
              {semester?.label && <span className={s.headerPill}>{semester.label} sem</span>}
            </div>
          )}
        </div>

        {/* Class switcher — only render when the teacher has > 1 offering.
            Sits in the header row so jumping between sections is one tap. */}
        {!loading && switcherItems.length > 1 && (
          <div className={s.switcher} ref={switcherRef}>
            <button
              type="button"
              className={s.switcherBtn}
              onClick={() => setSwitcherOpen(o => !o)}
              aria-haspopup="listbox"
              aria-expanded={switcherOpen}
              title="Jump to another class you teach">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.2"
                   strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="4" width="18" height="4" rx="1"/>
                <rect x="3" y="10" width="18" height="4" rx="1"/>
                <rect x="3" y="16" width="18" height="4" rx="1"/>
              </svg>
              <span className={s.switcherLabel}>Switch class</span>
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none"
                   className={switcherOpen ? s.switcherChevOpen : s.switcherChev}
                   aria-hidden="true">
                <path d="M2.5 4.5 L6 8 L9.5 4.5"
                      stroke="currentColor" strokeWidth="1.6"
                      strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {switcherOpen && (
              <div className={s.switcherMenu} role="listbox">
                <div className={s.switcherMenuHead}>
                  Your classes
                  <span className={s.switcherCount}>
                    {switcherItems.length} total
                  </span>
                </div>
                {switcherItems.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    role="option"
                    aria-selected={item.isCurrent}
                    className={`${s.switcherItem} ${item.isCurrent ? s.switcherItemActive : ""}`}
                    onClick={() => {
                      setSwitcherOpen(false);
                      if (!item.isCurrent) navigate(`/teacher/grades/${item.id}`);
                    }}>
                    <div className={s.switcherItemIcon} aria-hidden="true">
                      {item.code
                        ? item.code.slice(0, 2).toUpperCase()
                        : (item.title || "?").slice(0, 2).toUpperCase()}
                    </div>
                    <div className={s.switcherItemBody}>
                      <div className={s.switcherItemTitle} title={item.title}>
                        {item.title}
                      </div>
                      <div className={s.switcherItemMeta}>
                        {[item.sectionName && `Section ${item.sectionName}`,
                          item.semesterLabel && `${item.semesterLabel} sem`,
                          item.code]
                          .filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    {item.isCurrent && (
                      <span className={s.switcherItemBadge}>Current</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Draft-restore banner — appears when localStorage holds unsaved
          changes that differ from the freshly-loaded baseline. Renders
          above the encoder card so the choice can't be missed. */}
      {pendingDraft && !loading && (
        <div className={s.draftBanner} role="alertdialog" aria-live="polite">
          <div className={s.draftBannerIcon} aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9"/>
              <polyline points="12 7 12 12 15 14"/>
            </svg>
          </div>
          <div className={s.draftBannerBody}>
            <div className={s.draftBannerTitle}>
              Restore unsaved changes?
            </div>
            <div className={s.draftBannerSub}>
              {pendingDraft.dirtyCount} cell{pendingDraft.dirtyCount === 1 ? "" : "s"} were edited
              {pendingDraft.savedAt && (
                <> · last typed {new Date(pendingDraft.savedAt).toLocaleString()}</>
              )}.
            </div>
          </div>
          <div className={s.draftBannerActions}>
            <button
              type="button"
              className={s.draftBtnGhost}
              onClick={discardDraft}>
              Discard
            </button>
            <button
              type="button"
              className={s.draftBtnPrimary}
              onClick={applyDraft}>
              Restore changes
            </button>
          </div>
        </div>
      )}

      <div className={s.card}>
        {error && <div className={s.error}>{error}</div>}

        {/* Loading skeleton */}
        {loading && (
          <div className={s.skeleton}>
            <div className={s.skeletonRow} />
            <div className={s.skeletonRow} />
            <div className={s.skeletonRow} />
            <div className={s.skeletonRow} />
          </div>
        )}

        {/* Empty roster */}
        {!loading && students.length === 0 && (
          <div className={s.emptyPremium}>
            <div className={s.emptyPremiumTitle}>No students in this section yet.</div>
            <div className={s.emptyPremiumSub}>
              Enroll students from <strong>Directory → Enrollments</strong>; they'll appear here.
            </div>
          </div>
        )}

        {/* Roster + encoder */}
        {!loading && students.length > 0 && (
          <>
            {/* Live stats strip */}
            <div className={s.statsStrip}>
              <div className={s.statTile}>
                <div className={s.statTileLabel}>Roster</div>
                <div className={s.statTileValue}>{stats.total}</div>
                <div className={s.statTileSub}>student{stats.total === 1 ? "" : "s"}</div>
              </div>
              {stats.perPeriod.map(pp => (
                <div key={pp.code} className={s.statTile}>
                  <div className={s.statTileLabel}>{pp.label} encoded</div>
                  <div className={s.statTileValue}>{pp.encoded}<span style={{ color: "#b9af9a", fontWeight: 600 }}> / {stats.total}</span></div>
                  <div className={s.statTileSub}>avg {pp.avg}</div>
                </div>
              ))}
              <div className={s.statTile}>
                <div className={s.statTileLabel}>Unsaved</div>
                <div className={s.statTileValue}>{dirtyCount}</div>
                <div className={s.statTileSub}>{dirtyCount === 0 ? "all in sync" : "edited cell" + (dirtyCount === 1 ? "" : "s")}</div>
              </div>
            </div>

            {/* Period tabs — encode one quarter at a time, or review all of
                them side by side in Summary. */}
            <div className={s.periodTabs} role="tablist" aria-label="Grading period">
              {periods.map(per => {
                const complete = students.length > 0 && students.every(st => {
                  const sc = grades[st.id]?.[per.code]?.score;
                  return sc !== "" && sc != null;
                });
                const edited = students.some(st => isCellDirty(st.id, per.code));
                return (
                  <button key={per.code} type="button" role="tab" aria-selected={tab === per.code}
                          className={`${s.periodTab} ${tab === per.code ? s.periodTabActive : ""}`}
                          onClick={() => setTab(per.code)}>
                    {per.label}
                    {edited
                      ? <span className={s.periodTabDot} title="Unsaved changes" aria-label="unsaved changes" />
                      : complete && <span className={s.periodTabCheck} aria-label="complete">✓</span>}
                  </button>
                );
              })}
              <button type="button" role="tab" aria-selected={tab === "summary"}
                      className={`${s.periodTab} ${s.periodTabSummary} ${tab === "summary" ? s.periodTabActive : ""}`}
                      onClick={() => setTab("summary")}>
                Summary
              </button>
            </div>

            {tab !== "summary" && (<>
            {/* Keyboard help row — quietly tells the teacher how the
                inputs behave so a new user finds the shortcuts. */}
            <div className={s.kbdHelp}>
              <span><kbd>Enter</kbd> next student</span>
              <span><kbd>Shift</kbd> + <kbd>Enter</kbd> previous</span>
              <span>Paste a column from Excel / Sheets straight into a Score cell</span>
            </div>

            <div className={s.tableWrap}>
              <table className={`${s.table} ${s.tableUpgraded}`}>
                <thead>
                  <tr>
                    <th>Student</th>
                    {tabCodes.map(p => (
                      <th key={p} colSpan={2} className={s.colGroup}>
                        {labelOf(p)}
                      </th>
                    ))}
                  </tr>
                  <tr className={s.subHead}>
                    <th></th>
                    {tabCodes.flatMap(p => [
                      <th key={p + "-s"}>Score</th>,
                      <th key={p + "-r"}>Remarks</th>,
                    ])}
                  </tr>
                </thead>
                <tbody>
                  {students.map((st, idx) => {
                    const dirty = isRowDirty(st.id);
                    const fullName = `${st.firstName || ""} ${st.lastName || ""}`.trim();
                    return (
                      <tr
                        key={st.id}
                        className={dirty ? s.rowDirty : ""}
                        style={{ animationDelay: `${Math.min(idx, 12) * 25}ms` }}
                      >
                        <td>
                          <div className={s.studentCell}>
                            <span className={s.studentAvatar} style={{ background: avatarColor(fullName) }}>
                              {st.photoUrl
                                ? <img src={st.photoUrl} alt="" className={s.studentAvatarImg} />
                                : initials(st.firstName, st.lastName)}
                            </span>
                            <div>
                              <div className={s.studentName}>{st.lastName}, {st.firstName}</div>
                              <div className={s.studentMeta}>#{st.studentNumber}</div>
                            </div>
                          </div>
                        </td>
                        {tabCodes.flatMap(p => {
                          const cell = grades[st.id]?.[p] || { score: "", remarks: "" };
                          const tone = scoreToneClass(cell.score);
                          return [
                            <td key={`${st.id}-${p}-s`} className={s.scoreCell}>
                              <input
                                ref={el => setScoreRef(st.id, p, el)}
                                className={`${s.inputScorePremium} ${tone}`}
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                inputMode="decimal"
                                placeholder="—"
                                value={cell.score}
                                aria-label={`${labelOf(p)} score for ${fullName}`}
                                onChange={e => setCell(st.id, p, "score", e.target.value)}
                                onKeyDown={e => onScoreKeyDown(e, idx, p)}
                                onPaste={e => onScorePaste(e, idx, p)}
                              />
                            </td>,
                            <td key={`${st.id}-${p}-r`} className={s.remarksCell}>
                              <input
                                className={s.inputRemarksPremium}
                                type="text"
                                placeholder="Optional note…"
                                value={cell.remarks}
                                aria-label={`${labelOf(p)} remarks for ${fullName}`}
                                onChange={e => setCell(st.id, p, "remarks", e.target.value)}
                              />
                            </td>,
                          ];
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>)}

            {tab === "summary" && (
              <SummaryTable
                students={students}
                periods={periods}
                grades={grades}
                onOpen={setDetailStudent}
              />
            )}

            {/* Sticky save bar — only present while there are unsaved edits */}
            {dirtyCount > 0 && (
              <div className={s.stickyBar} role="region" aria-label="Unsaved changes">
                <div>
                  <div className={s.stickyBarLabel}>
                    {dirtyCount} unsaved change{dirtyCount === 1 ? "" : "s"}
                  </div>
                  <span className={s.stickyBarHint}>
                    Save to commit · auto-saved locally as you type
                  </span>
                </div>
                <button
                  type="button"
                  className={s.btnPrimaryPremium}
                  onClick={saveAll}
                  disabled={busy}
                >
                  {busy ? "Saving…" : "Save grades"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {detailStudent && (
        <StudentGradeModal
          student={detailStudent}
          periods={periods}
          cell={grades[detailStudent.id] || {}}
          subjectTitle={headingTitle}
          onEdit={(code) => { setDetailStudent(null); setTab(code); }}
          onClose={() => setDetailStudent(null)}
        />
      )}

      {/* Auto-dismissing toast — keyed so a second save re-triggers the animation */}
      {toast && (
        <div
          key={`${toast.kind}-${toast.text}`}
          role="status"
          className={`${s.toast} ${toast.kind === "error" ? s.toastError : s.toastSuccess}`}>
          {toast.text}
        </div>
      )}
    </div>
  );
}

/** Count cell-level differences between two grade maps (the loaded server
 *  baseline vs a stored draft). Pure helper — extracted so the draft-
 *  probe block in the data-load effect stays readable. */
function countCellDiff(a, b, codes) {
  let n = 0;
  const ids = new Set([
    ...Object.keys(a || {}),
    ...Object.keys(b || {}),
  ]);
  for (const id of ids) {
    for (const p of codes) {
      const ax = a?.[id]?.[p] || { score: "", remarks: "" };
      const bx = b?.[id]?.[p] || { score: "", remarks: "" };
      if ((ax.score || "") !== (bx.score || "") ||
          (ax.remarks || "") !== (bx.remarks || "")) n++;
    }
  }
  return n;
}

/** Final grade + status for one student from the (possibly unsaved) cells.
 *  Final = mean of the class's own periods that have scores; PASSED/FAILED
 *  only once every period is in. */
function finalFor(cell, periods) {
  const own = periods.filter(per => !per.legacy).map(per => per.code);
  const final = averageOf(cell, own);
  const complete = own.length > 0 && own.every(c => cell?.[c]?.score !== "" && cell?.[c]?.score != null);
  const status = !complete ? "progress" : final >= 75 ? "passed" : "failed";
  return { final, status };
}

/** Summary tab — every period side by side, read-only. Click a row for
 *  that student's full record. */
function SummaryTable({ students, periods, grades, onOpen }) {
  return (
    <div className={s.tableWrap}>
      <table className={`${s.table} ${s.tableUpgraded} ${s.summaryTable}`}>
        <thead>
          <tr>
            <th>Student</th>
            {periods.map(per => <th key={per.code} className={s.summaryNum}>{per.label}</th>)}
            <th className={s.summaryNum}>Final</th>
            <th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          {students.map(st => {
            const cell = grades[st.id] || {};
            const { final, status } = finalFor(cell, periods);
            return (
              <tr key={st.id} className={s.summaryRow} tabIndex={0}
                  onClick={() => onOpen(st)}
                  onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(st); } }}>
                <td>
                  <div className={s.studentName}>{st.lastName}, {st.firstName}</div>
                  <div className={s.studentMeta}>#{st.studentNumber}</div>
                </td>
                {periods.map(per => {
                  const sc = cell[per.code]?.score;
                  return (
                    <td key={per.code} className={s.summaryNum}>
                      {sc === "" || sc == null ? <span className={s.summaryBlank}>—</span> : sc}
                    </td>
                  );
                })}
                <td className={`${s.summaryNum} ${s.summaryFinal}`}>
                  {final == null ? "—" : final.toFixed(1)}
                </td>
                <td>
                  <span className={`${s.statusTag} ${s["statusTag_" + status]}`}>
                    {status === "passed" ? "PASSED" : status === "failed" ? "FAILED" : "In progress"}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** One student's full record for this subject — every period's score and
 *  remarks, the final grade and DepEd descriptor. */
function StudentGradeModal({ student, periods, cell, subjectTitle, onEdit, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const { final, status } = finalFor(cell, periods);
  const desc = descriptorFor(final);
  return (
    <div className={s.gmOverlay} onClick={onClose}>
      <div className={s.gmModal} role="dialog" aria-modal="true" aria-labelledby="gm-title"
           onClick={e => e.stopPropagation()}>
        <div className={s.gmHead}>
          <div>
            <div className={s.gmEyebrow}>{subjectTitle}</div>
            <h3 id="gm-title" className={s.gmTitle}>{student.lastName}, {student.firstName}</h3>
            <div className={s.gmSub}>#{student.studentNumber}</div>
          </div>
          <button type="button" className={s.gmClose} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className={s.gmBody}>
          {periods.map(per => {
            const c = cell[per.code] || {};
            const has = c.score !== "" && c.score != null;
            return (
              <div key={per.code} className={s.gmRow}>
                <div className={s.gmPeriod}>{per.label}</div>
                <div className={s.gmScore}>{has ? c.score : "—"}</div>
                <div className={s.gmRemarks}>{c.remarks || <span className={s.summaryBlank}>No remarks</span>}</div>
                {!per.legacy && (
                  <button type="button" className={s.gmEdit} onClick={() => onEdit(per.code)}>Edit</button>
                )}
              </div>
            );
          })}
        </div>

        <div className={s.gmFinal}>
          <div>
            <div className={s.gmFinalLabel}>Final grade</div>
            <div className={s.gmFinalValue}>{final == null ? "—" : final.toFixed(1)}</div>
          </div>
          <div className={s.gmFinalRight}>
            <span className={`${s.statusTag} ${s["statusTag_" + status]}`}>
              {status === "passed" ? "PASSED" : status === "failed" ? "FAILED" : "In progress"}
            </span>
            {final != null && <div className={s.gmDesc}>{desc.label}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
